// Synthesized incoming-call ringtones (web) — no audio assets, generated live
// with the WebAudio oscillator engine in ./reconnectTone.ts. The
// catalog is deliberately chiptune-flavoured (square/triangle waves, short
// arpeggio loops) the way old-school game composers worked within a couple of
// pulse channels. Each is a repeating TonePattern; `intervalMs` is the whole
// loop length including its trailing gap.
//
// Same definitions are rendered to bundled audio files for Android (which plays
// its ring natively) by tools/gen-ringtones — so both clients ring the same.
// Adding a ringtone here is additive: never renumber or repurpose an existing
// `id`, or a user's saved choice would silently become a different tone.

import {
  createPatternTone,
  defaultCtor,
  type AudioContextCtor,
  type TonePattern,
  type TonePlayer,
} from './reconnectTone.js';

// Translation keys used by ringtone display names. Kept as a local
// string-literal type (a structural subset of i18n's `MessageKey`) so the
// brain package never depends on app-owned `i18n/`; every literal below is a
// real key in i18n's dictionaries (screens/settings/WebRingtonePicker.tsx
// passes `ringtone.nameKey` straight into `t()`, which still typechecks since
// this narrower union is assignable to the wider `MessageKey`).
export type RingtoneMessageKey =
  | 'ringtone.chime'
  | 'ringtone.arcade'
  | 'ringtone.blip'
  | 'ringtone.pulse'
  | 'ringtone.crystal'
  | 'ringtone.coin'
  | 'ringtone.march'
  | 'ringtone.bounce'
  | 'ringtone.laser'
  | 'ringtone.fanfare';

// Equal-temperament note frequencies (Hz) used by the catalog below.
const N = {
  G4: 392.0,
  A4: 440.0,
  B4: 493.88,
  C5: 523.25,
  D5: 587.33,
  E5: 659.25,
  F5: 698.46,
  G5: 783.99,
  A5: 880.0,
  B5: 987.77,
  C6: 1046.5,
  D6: 1174.66,
  E6: 1318.51,
  G6: 1567.98,
} as const;

/** Compact note builder: pulse-channel note at `at`s for `dur`s. */
const n = (
  at: number,
  dur: number,
  freqHz: number,
  wave: OscillatorType = 'square',
  gain = 0.13,
): TonePattern['notes'][number] => ({ atS: at, durS: dur, freqHz, gain, wave });

export interface Ringtone {
  id: string;
  nameKey: RingtoneMessageKey;
  pattern: TonePattern;
}

export const DEFAULT_RINGTONE_ID = 'chime';

// The classic sine chime is the default (what the app rang before). Everything
// after it is the chiptune set.
export const RINGTONES: readonly Ringtone[] = [
  {
    id: 'chime',
    nameKey: 'ringtone.chime',
    pattern: {
      intervalMs: 2_500,
      notes: [
        { atS: 0, durS: 0.3, freqHz: N.F5, gain: 0.15, wave: 'sine' },
        { atS: 0.45, durS: 0.3, freqHz: N.A5, gain: 0.15, wave: 'sine' },
      ],
    },
  },
  {
    id: 'arcade',
    nameKey: 'ringtone.arcade',
    // Power-up: ascending arpeggio, twice.
    pattern: {
      intervalMs: 2_600,
      notes: [
        n(0.0, 0.09, N.C5), n(0.1, 0.09, N.E5), n(0.2, 0.09, N.G5), n(0.3, 0.16, N.C6),
        n(0.6, 0.09, N.C5), n(0.7, 0.09, N.E5), n(0.8, 0.09, N.G5), n(0.9, 0.22, N.C6),
      ],
    },
  },
  {
    id: 'blip',
    nameKey: 'ringtone.blip',
    // Handheld menu double-blip, high and dry.
    pattern: {
      intervalMs: 1_800,
      notes: [n(0.0, 0.07, N.E6), n(0.12, 0.07, N.E6), n(0.28, 0.1, N.B5)],
    },
  },
  {
    id: 'pulse',
    nameKey: 'ringtone.pulse',
    // Two-tone alarm alternation.
    pattern: {
      intervalMs: 2_200,
      notes: [
        n(0.0, 0.18, N.A5), n(0.22, 0.18, N.E5),
        n(0.44, 0.18, N.A5), n(0.66, 0.18, N.E5),
      ],
    },
  },
  {
    id: 'crystal',
    nameKey: 'ringtone.crystal',
    // Bell-like descending triangle.
    pattern: {
      intervalMs: 2_800,
      notes: [
        n(0.0, 0.14, N.C6, 'triangle', 0.16), n(0.16, 0.14, N.G5, 'triangle', 0.16),
        n(0.32, 0.14, N.E5, 'triangle', 0.16), n(0.48, 0.3, N.C5, 'triangle', 0.16),
      ],
    },
  },
  {
    id: 'coin',
    nameKey: 'ringtone.coin',
    // The classic two-note pickup, repeated.
    pattern: {
      intervalMs: 2_000,
      notes: [
        n(0.0, 0.08, N.B5), n(0.09, 0.28, N.E6),
        n(0.6, 0.08, N.B5), n(0.69, 0.3, N.E6),
      ],
    },
  },
  {
    id: 'march',
    nameKey: 'ringtone.march',
    // A short marching motif.
    pattern: {
      intervalMs: 2_600,
      notes: [
        n(0.0, 0.12, N.G4), n(0.16, 0.12, N.G4), n(0.32, 0.12, N.C5),
        n(0.5, 0.14, N.E5), n(0.7, 0.22, N.G5),
      ],
    },
  },
  {
    id: 'bounce',
    nameKey: 'ringtone.bounce',
    // Octave bounce, triangle.
    pattern: {
      intervalMs: 2_200,
      notes: [
        n(0.0, 0.1, N.C5, 'triangle'), n(0.14, 0.1, N.C6, 'triangle'),
        n(0.32, 0.1, N.C5, 'triangle'), n(0.46, 0.16, N.C6, 'triangle'),
      ],
    },
  },
  {
    id: 'laser',
    nameKey: 'ringtone.laser',
    // Descending zap steps.
    pattern: {
      intervalMs: 2_000,
      notes: [
        n(0.0, 0.08, N.D6), n(0.08, 0.08, N.A5), n(0.16, 0.08, N.F5), n(0.24, 0.16, N.D5),
      ],
    },
  },
  {
    id: 'fanfare',
    nameKey: 'ringtone.fanfare',
    // Triumphant little arpeggio flourish.
    pattern: {
      intervalMs: 3_000,
      notes: [
        n(0.0, 0.1, N.C5), n(0.12, 0.1, N.E5), n(0.24, 0.1, N.G5),
        n(0.36, 0.14, N.C6), n(0.54, 0.1, N.G5), n(0.66, 0.28, N.C6),
      ],
    },
  },
];

const BY_ID = new Map(RINGTONES.map((r) => [r.id, r]));

/** The chosen ringtone, or the default when the id is unknown/absent. */
export function ringtoneById(id: string | null | undefined): Ringtone {
  return (id != null && BY_ID.get(id)) || BY_ID.get(DEFAULT_RINGTONE_ID)!;
}

/** A player for a ringtone id (falls back to the default). */
export function createRingtoneFor(
  id: string | null | undefined,
  getCtor: () => AudioContextCtor | undefined = defaultCtor,
): TonePlayer {
  return createPatternTone(ringtoneById(id).pattern, getCtor);
}
